/**
 * MyNoteEditor — player-facing note editor for `/lore/my-notes/...`.
 *
 * Intentionally simpler than LoreEditor: no visibility controls, no section
 * gating, no draft sidebar — player notes are implicitly private to author +
 * DM and live in their own S3 prefix. Just a textarea, a debounced direct
 * save, and a delete button.
 *
 * Saves go straight to S3 via `putMyNote` (no draft round-trip — unlike the
 * DM lore editor, there's no "publish" step here, the player IS the
 * authority on their own notes). The first save of a brand-new note omits
 * `baseEtag` so the server existence-probes; subsequent saves carry the
 * etag returned by the previous PUT for optimistic concurrency.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteMyNote,
  getMyNote,
  LoreConflictError,
  putMyNote,
} from '../lib/loreApi';
import './LoreEditor.css';

interface Props {
  slug: string;
  /** True if this is a freshly-created note that hasn't been saved yet. */
  isNew?: boolean;
  /** Initial body for a brand-new note (e.g. a starter template). */
  initialContent?: string;
  /** Called after a successful delete so the parent can drop the selection. */
  onDeleted: () => void;
  /** Called after the first successful save of a brand-new note. */
  onCreated?: () => void;
}

const SaveDebounceMs = 1000;

export default function MyNoteEditor({
  slug,
  isNew = false,
  initialContent = '',
  onDeleted,
  onCreated,
}: Props) {
  const [content, setContent] = useState<string>(initialContent);
  const [etag, setEtag] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(!isNew);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [conflict, setConflict] = useState<{ currentBody: string; currentEtag: string } | null>(null);
  // True until the first PUT for an isNew note succeeds. Tracked separately
  // from the prop so we can flip it locally on first save.
  const [neverSaved, setNeverSaved] = useState<boolean>(isNew);

  // Debounce timer + ownership guard so a stale debounce from a previous
  // slug can't bleed into the freshly-loaded one.
  const saveTimer = useRef<number | null>(null);
  const slugOwner = useRef<string>(slug);

  // Reset state when the slug changes (parent picked a different note).
  useEffect(() => {
    slugOwner.current = slug;
    setError(null);
    setConflict(null);
    setSavedAt(null);
    setSaving(false);
    if (isNew) {
      setContent(initialContent);
      setEtag('');
      setLoading(false);
      setNeverSaved(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getMyNote(slug)
      .then((n) => {
        if (cancelled || slugOwner.current !== slug) return;
        setContent(n.content);
        setEtag(n.etag);
        setNeverSaved(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (saveTimer.current != null) {
        window.clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
    };
  }, [slug, isNew, initialContent]);

  const doSave = useCallback(async () => {
    if (slugOwner.current !== slug) return;
    setSaving(true);
    setError(null);
    try {
      // Brand-new note: omit baseEtag so the server existence-probes and
      // refuses 409 if the slug collides with an existing note. After the
      // first successful save we have an etag and switch to update mode.
      const resp = await putMyNote(slug, content, neverSaved ? undefined : etag);
      if (slugOwner.current !== slug) return;
      setEtag(resp.etag);
      const now = new Date();
      setSavedAt(
        now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      );
      if (neverSaved) {
        setNeverSaved(false);
        onCreated?.();
      }
    } catch (e) {
      if (e instanceof LoreConflictError) {
        setConflict({
          currentBody: e.conflict.currentBody,
          currentEtag: e.conflict.currentEtag,
        });
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      if (slugOwner.current === slug) setSaving(false);
    }
  }, [slug, content, etag, neverSaved, onCreated]);

  // Debounced autosave on content change. Skip while loading the initial
  // body so the load itself doesn't trigger a save with the wrong owner.
  useEffect(() => {
    if (loading) return;
    if (saveTimer.current != null) {
      window.clearTimeout(saveTimer.current);
    }
    saveTimer.current = window.setTimeout(() => {
      void doSave();
    }, SaveDebounceMs);
    return () => {
      if (saveTimer.current != null) {
        window.clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
    };
    // doSave intentionally NOT in deps — including it would re-run the timer
    // on every keystroke for the wrong reason. We only want the timer to
    // re-arm when content changes; doSave reads fresh state via closure each
    // time it's actually invoked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, loading]);

  const handleDelete = useCallback(async () => {
    if (neverSaved) {
      // Nothing to delete server-side; just bail.
      onDeleted();
      return;
    }
    if (!window.confirm(`Delete note "${slug}"? This cannot be undone.`)) return;
    try {
      await deleteMyNote(slug);
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [slug, neverSaved, onDeleted]);

  if (loading) {
    return <div className="lore-editor lore-empty">Loading note…</div>;
  }

  return (
    <section className="lore-editor lore-editor--my-notes">
      <header className="lore-editor__header">
        <div>
          <h3>My note</h3>
          <small>{slug}</small>
        </div>
        <div className="lore-editor__status">
          {saving && <span className="lore-editor__busy">Saving…</span>}
          {!saving && savedAt && <span className="lore-editor__saved">Saved {savedAt}</span>}
          {!saving && !savedAt && neverSaved && <span className="lore-editor__draft-badge">Unsaved</span>}
        </div>
      </header>

      {error && <div className="lore-error">{error}</div>}

      {conflict && (
        <div className="lore-editor__conflict">
          <p>
            This note was modified somewhere else (DM via Obsidian, or another
            tab). Pick which copy to keep:
          </p>
          <div>
            <button
              type="button"
              onClick={() => {
                setContent(conflict.currentBody);
                setEtag(conflict.currentEtag);
                setConflict(null);
              }}
            >
              Replace editor with vault copy
            </button>
            <button
              type="button"
              onClick={() => {
                // Adopt the new etag so the next save overwrites the vault
                // copy with the editor's current content.
                setEtag(conflict.currentEtag);
                setConflict(null);
              }}
            >
              Keep editing my version
            </button>
          </div>
        </div>
      )}

      <textarea
        className="lore-editor__textarea"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        spellCheck
        rows={24}
      />

      <div className="lore-editor__actions">
        <button
          type="button"
          onClick={() => void doSave()}
          disabled={saving}
        >
          Save now
        </button>
        <button
          type="button"
          onClick={() => void handleDelete()}
        >
          Delete
        </button>
      </div>
    </section>
  );
}
